import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Lock, LogOut, MapPin, Plus, RotateCcw, Send } from "lucide-react";
import { LogoMark } from "../components/Logo";
import { QUESTIONS, SECTORS, SURVEYORS } from "../survey/questions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pendli-survey-token";
const SURVEYOR_KEY = "pendli-survey-surveyor";
const SECTOR_KEY = "pendli-survey-sector";

const OptionButton = ({ label, selected, onClick, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`w-full text-left min-h-[68px] px-5 py-4 border text-[17px] leading-snug font-medium transition-colors active:scale-[0.99] ${
      selected
        ? "bg-amber text-ink border-amber"
        : "bg-amber-deep/40 text-cream border-amber/25 hover:border-amber/60"
    }`}
  >
    <span className="flex items-center justify-between gap-3">
      <span>{label}</span>
      {selected && <Check size={20} className="shrink-0" />}
    </span>
  </button>
);

const Pill = ({ label, selected, onClick, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`h-12 px-4 border text-sm font-bold tracking-wide uppercase transition-colors ${
      selected ? "bg-amber text-ink border-amber" : "bg-transparent text-cream/70 border-amber/30"
    }`}
  >
    {label}
  </button>
);

function PinGate({ onAuthed }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/survey/auth`, { pin });
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuthed();
    } catch (err) {
      setError(err?.response?.data?.detail || "Kunne ikke logge inn.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="px-6 pt-16 pb-10 max-w-md mx-auto" data-testid="survey-pin-form">
      <div className="w-12 h-12 bg-amber-deep border border-amber/40 flex items-center justify-center mb-6">
        <Lock size={20} className="text-amber" />
      </div>
      <h1 className="font-display text-4xl font-extrabold tracking-tighter" style={{ letterSpacing: "-0.04em" }}>
        Dør-til-dør
      </h1>
      <p className="text-cream/60 text-sm mt-2 mb-8">Internt verktøy. Skriv inn PIN for å starte.</p>
      <input
        type="tel"
        inputMode="numeric"
        autoFocus
        data-testid="survey-pin-input"
        value={pin}
        onChange={(e) => setPin(e.target.value)}
        placeholder="PIN"
        className="w-full bg-transparent border-b border-cream/20 focus:border-amber py-4 text-2xl tracking-[0.3em] text-cream placeholder:text-cream/25"
      />
      {error && <p className="mt-4 text-orange text-sm" data-testid="survey-pin-error">{error}</p>}
      <button
        type="submit"
        disabled={loading || !pin}
        data-testid="survey-pin-submit"
        className="mt-8 w-full h-14 bg-amber text-ink font-display font-bold text-lg disabled:opacity-50"
      >
        {loading ? "Sjekker..." : "Åpne undersøkelsen →"}
      </button>
    </form>
  );
}

export default function Survey() {
  const [screen, setScreen] = useState(localStorage.getItem(TOKEN_KEY) ? "setup" : "pin");
  const [surveyor, setSurveyor] = useState(localStorage.getItem(SURVEYOR_KEY) || "");
  const [sector, setSector] = useState(localStorage.getItem(SECTOR_KEY) || "");
  const [street, setStreet] = useState("");
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Pendli — Dør-til-dør";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  // Expired / invalidated tokens must land on the PIN screen, not fail at submit.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    axios
      .get(`${API}/survey/me`, { headers: { Authorization: `Bearer ${token}` } })
      .catch((err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setScreen("pin");
        }
      });
  }, []);

  const q = QUESTIONS[idx];
  const value = answers[q?.id];
  const noParking = answers.parking_type === "none";
  const isLast = idx === QUESTIONS.length - 1;

  const pick = (field, v) => {
    setAnswers((prev) => {
      const next = { ...prev, [field]: v };
      if (field === "main_barrier" && v !== "other") next.main_barrier_other = "";
      if (field === "contact_permission" && v !== "yes") {
        next.contact_name = "";
        next.contact_phone = "";
        next.contact_email = "";
      }
      return next;
    });
    const stays =
      (field === "parking_type" && v === "none") ||
      (field === "main_barrier" && v === "other") ||
      field === "contact_permission";
    if (!stays) setTimeout(() => setIdx((i) => Math.min(i + 1, QUESTIONS.length - 1)), 160);
  };

  const canProceed = () => {
    if (!value) return false;
    if (q.id === "contact_permission" && value === "yes") {
      const hasName = (answers.contact_name || "").trim().length > 0;
      const hasContact =
        (answers.contact_phone || "").trim().length > 0 || (answers.contact_email || "").trim().length > 0;
      return hasName && hasContact;
    }
    return true;
  };

  const submit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    const payload = {
      surveyor,
      sector,
      street,
      parking_type: answers.parking_type,
      walking_distance: answers.walking_distance || null,
      weekday_availability: answers.weekday_availability || null,
      pendli_interest: answers.pendli_interest || null,
      main_barrier: answers.main_barrier || null,
      main_barrier_other: (answers.main_barrier_other || "").trim() || null,
      acceptable_monthly_income: answers.acceptable_monthly_income || null,
      parking_decision_maker: answers.parking_decision_maker || null,
      contact_permission: answers.contact_permission || null,
      contact_name: (answers.contact_name || "").trim() || null,
      contact_phone: (answers.contact_phone || "").trim() || null,
      contact_email: (answers.contact_email || "").trim() || null,
    };
    try {
      await axios.post(`${API}/survey/responses`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` },
      });
      setScreen("done");
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setScreen("pin");
        return;
      }
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Klarte ikke lagre svaret. Prøv igjen.");
    } finally {
      setSubmitting(false);
    }
  }, [answers, sector, street, surveyor]);

  const newSurvey = () => {
    setAnswers({});
    setStreet("");
    setIdx(0);
    setError(null);
    setScreen("setup");
  };

  const chooseSurveyor = (v) => {
    setSurveyor(v);
    localStorage.setItem(SURVEYOR_KEY, v);
  };
  const chooseSector = (v) => {
    setSector(v);
    localStorage.setItem(SECTOR_KEY, v);
  };

  // Explicit logout: drop the token and the remembered surveyor/sector so the
  // next person starts clean. The PIN itself is never stored.
  const logout = () => {
    [TOKEN_KEY, SURVEYOR_KEY, SECTOR_KEY].forEach((k) => localStorage.removeItem(k));
    setSurveyor("");
    setSector("");
    setStreet("");
    setAnswers({});
    setIdx(0);
    setError(null);
    setScreen("pin");
  };

  const LogoutButton = ({ className = "", testId = "survey-logout" }) => (
    <button
      type="button"
      onClick={logout}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-cream/45 hover:text-orange transition-colors ${className}`}
    >
      <LogOut size={12} /> Logg ut
    </button>
  );

  if (screen === "pin") {
    return (
      <main className="min-h-screen bg-ink text-cream" data-testid="survey-page">
        <PinGate onAuthed={() => setScreen("setup")} />
      </main>
    );
  }

  if (screen === "done") {
    return (
      <main className="min-h-screen bg-ink text-cream flex flex-col items-center justify-center px-6" data-testid="survey-done">
        <div className="w-20 h-20 bg-amber flex items-center justify-center mb-8">
          <Check size={44} className="text-ink" strokeWidth={3} />
        </div>
        <h1 className="font-display text-5xl font-extrabold tracking-tighter text-center" style={{ letterSpacing: "-0.04em" }}>
          Svar lagret
        </h1>
        <p className="text-cream/60 mt-3 text-center">
          {SURVEYORS.find((s) => s.v === surveyor)?.l} · {SECTORS.find((s) => s.v === sector)?.l}
        </p>
        <button
          onClick={newSurvey}
          data-testid="survey-new"
          className="mt-12 w-full max-w-sm h-16 bg-amber text-ink font-display font-bold text-xl inline-flex items-center justify-center gap-3"
        >
          <Plus size={22} strokeWidth={3} /> Ny undersøkelse
        </button>
        <LogoutButton testId="survey-logout-done" className="mt-8" />
      </main>
    );
  }

  if (screen === "setup") {
    return (
      <main className="min-h-screen bg-ink text-cream pb-28" data-testid="survey-setup">
        <div className="px-6 pt-10 max-w-md mx-auto">
          <div className="flex items-center justify-between gap-3 mb-8">
            <div className="flex items-center gap-2.5">
              <LogoMark size={24} />
              <span className="font-display text-lg font-extrabold tracking-tight">Dør-til-dør · Asker</span>
            </div>
            <LogoutButton />
          </div>

          <p className="text-xs font-bold tracking-widest uppercase text-amber/80 mb-3">Kartlegger</p>
          <div className="flex gap-3 mb-8">
            {SURVEYORS.map((s) => (
              <Pill
                key={s.v}
                testId={`surveyor-${s.v}`}
                label={s.l}
                selected={surveyor === s.v}
                onClick={() => chooseSurveyor(s.v)}
              />
            ))}
          </div>

          <p className="text-xs font-bold tracking-widest uppercase text-amber/80 mb-3">Sektor</p>
          <div className="flex flex-wrap gap-3 mb-8">
            {SECTORS.map((s) => (
              <Pill
                key={s.v}
                testId={`sector-${s.v}`}
                label={s.l}
                selected={sector === s.v}
                onClick={() => chooseSector(s.v)}
              />
            ))}
          </div>

          <p className="text-xs font-bold tracking-widest uppercase text-amber/80 mb-3">Gate</p>
          <div className="relative">
            <MapPin size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-cream/40" />
            <input
              type="text"
              data-testid="survey-street"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              placeholder="Gatenavn (ingen husnummer)"
              className="w-full bg-transparent border-b border-cream/20 focus:border-amber py-4 pl-7 text-lg text-cream placeholder:text-cream/25"
            />
          </div>
          <p className="text-xs text-cream/40 mt-3">Husnummer skal ikke registreres.</p>
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-ink/95 backdrop-blur border-t border-amber/15 px-6 py-4">
          <button
            onClick={() => setScreen("question")}
            disabled={!surveyor || !sector || !street.trim()}
            data-testid="survey-start"
            className="w-full max-w-md mx-auto h-16 bg-amber text-ink font-display font-bold text-xl flex items-center justify-center gap-2 disabled:opacity-40"
          >
            Start undersøkelse <ArrowRight size={22} strokeWidth={3} />
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-ink text-cream pb-32" data-testid="survey-question-screen">
      {/* Context bar — surveyor/sector always visible & changeable */}
      <div className="sticky top-0 z-30 bg-ink/95 backdrop-blur border-b border-amber/15">
        <div className="max-w-md mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-bold tracking-widest uppercase text-amber/80 truncate">
              {SURVEYORS.find((s) => s.v === surveyor)?.l} · {SECTORS.find((s) => s.v === sector)?.l}
            </p>
            <p className="text-sm text-cream/60 truncate">{street}</p>
          </div>
          <button
            onClick={() => setScreen("setup")}
            data-testid="survey-edit-context"
            className="h-10 px-3 border border-amber/30 text-xs font-bold uppercase tracking-widest text-cream/70 shrink-0"
          >
            Endre
          </button>
        </div>
        <div className="max-w-md mx-auto px-5 pb-3">
          <div className="flex items-center justify-between text-xs text-cream/50 mb-2">
            <span data-testid="survey-progress">Spørsmål {idx + 1} av {QUESTIONS.length}</span>
            {noParking && <span className="text-orange font-bold uppercase tracking-widest">Ingen plass</span>}
          </div>
          <div className="h-1 bg-amber/15">
            <div className="h-full bg-amber transition-all" style={{ width: `${((idx + 1) / QUESTIONS.length) * 100}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-5 pt-7">
        <h2 className="font-display text-[26px] leading-[1.15] font-extrabold tracking-tight mb-7" style={{ letterSpacing: "-0.03em" }}>
          {q.q}
        </h2>

        <div className="space-y-3">
          {q.options.map((o) => (
            <OptionButton
              key={o.v}
              testId={`opt-${q.id}-${o.v}`}
              label={o.l}
              selected={value === o.v}
              onClick={() => pick(q.id, o.v)}
            />
          ))}
        </div>

        {q.id === "main_barrier" && value === "other" && (
          <div className="mt-5 border-t border-amber/15 pt-5" data-testid="survey-barrier-other-wrap">
            <p className="text-xs font-bold tracking-widest uppercase text-amber/80 mb-2">Beskriv annet hinder</p>
            <textarea
              autoFocus
              rows={3}
              data-testid="survey-barrier-other"
              value={answers.main_barrier_other || ""}
              onChange={(e) => setAnswers((p) => ({ ...p, main_barrier_other: e.target.value }))}
              placeholder="Annet – skriv her"
              className="w-full bg-amber-deep/40 border border-amber/25 focus:border-amber px-4 py-3 text-lg text-cream placeholder:text-cream/25 resize-none"
            />
          </div>
        )}

        {q.id === "contact_permission" && value === "yes" && (
          <div className="mt-6 space-y-4 border-t border-amber/15 pt-6" data-testid="survey-contact-fields">
            <p className="text-xs text-cream/50">Spør muntlig om tillatelse før du fyller inn. Navn + telefon eller e-post.</p>
            {[
              { k: "contact_name", p: "Navn", t: "text", tid: "survey-contact-name" },
              { k: "contact_phone", p: "Telefon", t: "tel", tid: "survey-contact-phone" },
              { k: "contact_email", p: "E-post", t: "email", tid: "survey-contact-email" },
            ].map((f) => (
              <input
                key={f.k}
                type={f.t}
                data-testid={f.tid}
                value={answers[f.k] || ""}
                onChange={(e) => setAnswers((p) => ({ ...p, [f.k]: e.target.value }))}
                placeholder={f.p}
                className="w-full bg-transparent border-b border-cream/20 focus:border-amber py-3 text-lg text-cream placeholder:text-cream/25"
              />
            ))}
          </div>
        )}

        {noParking && q.id === "parking_type" && (
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="survey-early-exit"
            className="mt-7 w-full min-h-[64px] bg-amber text-ink font-display font-bold text-lg px-5 disabled:opacity-50"
          >
            {submitting ? "Lagrer..." : "Takk – avslutt undersøkelsen"}
          </button>
        )}

        {error && <p className="mt-5 text-orange text-sm" data-testid="survey-error">{error}</p>}
      </div>

      {/* Bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-ink/95 backdrop-blur border-t border-amber/15">
        <div className="max-w-md mx-auto px-5 py-4 flex items-center gap-3">
          <button
            onClick={() => (idx === 0 ? setScreen("setup") : setIdx(idx - 1))}
            data-testid="survey-back"
            className="h-14 px-5 border border-amber/30 text-cream/80 font-bold text-sm uppercase tracking-widest flex items-center gap-2"
          >
            <ArrowLeft size={18} /> Tilbake
          </button>
          {isLast ? (
            <button
              onClick={submit}
              disabled={!canProceed() || submitting}
              data-testid="survey-submit"
              className="flex-1 h-14 bg-amber text-ink font-display font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <Send size={18} strokeWidth={2.5} /> {submitting ? "Lagrer..." : "Send undersøkelse"}
            </button>
          ) : (
            <button
              onClick={() => setIdx(idx + 1)}
              disabled={!canProceed() || noParking}
              data-testid="survey-next"
              className="flex-1 h-14 bg-amber text-ink font-display font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-40"
            >
              Neste <ArrowRight size={20} strokeWidth={3} />
            </button>
          )}
        </div>
        <button
          onClick={newSurvey}
          data-testid="survey-restart"
          className="w-full pb-3 text-[11px] text-cream/35 uppercase tracking-widest inline-flex items-center justify-center gap-1.5"
        >
          <RotateCcw size={11} /> Nullstill
        </button>
        <div className="flex items-center justify-center gap-4 pb-3 -mt-2">
          <LogoutButton testId="survey-logout-questions" />
        </div>
      </div>
    </main>
  );
}
